import axios from "axios";
import { authHeader } from "@/helpers/authHeader.js";
import { KUrls } from "@/helpers/constants";

export const userService = {
  fetchMenu,
  updateUserPassword,
  updateOfficePECPassword,
  getOfficesPEC,
  changePasswordDuringLogin,
  getPasswordPolicy,
};

const userEndpoint = "/user";

function getAuthHeader() {
  const authorization = authHeader();
  return authorization;
}

function handleResponse(response) {
  const data = response && response.data;
  if (!response.status && response.status != 200) {
    if (response.status === 401) {
      // unauthorized
    }
    const error = (data && data.message) || response.statusText;
    return Promise.reject(error);
  }

  return data;
}

async function fetchMenu() {
  try {
    const authorization = getAuthHeader();
    const response = await axios.get(
      `${KUrls.BASE_REST_URL}${userEndpoint}/menu`,
      {
        headers: {
          // "Content-Type": "application/json",
          Authorization: authorization,
        },
      }
    );
    const data = await handleResponse(response);
    return data;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
}

async function updateUserPassword(oldPassword, newPassword) {
  try {
    const authorization = getAuthHeader();
    const response = await axios.put(
      `${KUrls.BASE_REST_URL}${userEndpoint}/password`,
      {
        oldPassword,
        newPassword,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: authorization,
        },
      }
    );
    if (response.status !== 204) {
      return false;
    } else {
      // usually it's 204 No-Content
      return true;
    }
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
}

async function updateOfficePECPassword(company, officeId, newPassword) {
  try {
    const authorization = getAuthHeader();
    const response = await axios.put(
      `${KUrls.BASE_REST_URL}${userEndpoint}/pec/${company}/password`,
      {
        officeId,
        newPassword,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: authorization,
        },
      }
    );
    if (response.status !== 204) {
      return false;
    } else {
      // usually it's 204 No-Content
      return true;
    }
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
}

async function getOfficesPEC(company) {
  try {
    const authorization = getAuthHeader();
    const response = await axios.get(
      `${KUrls.BASE_REST_URL}${userEndpoint}/pec/${company}/offices`,
      {
        headers: {
          // "Content-Type": "application/json",
          Authorization: authorization,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
}

/**
 * Change password during login enforcement scenario
 * Uses temporary token for authentication during password change process
 * @param {string} oldPassword - Current password
 * @param {string} newPassword - New password to set
 * @param {string} temporaryToken - Temporary JWT token for password change operations
 * @returns {Promise} - Promise resolving to authentication response with permanent token
 */
async function changePasswordDuringLogin(
  oldPassword,
  newPassword,
  temporaryToken
) {
  try {
    const response = await axios.put(
      `${KUrls.BASE_REST_URL}${userEndpoint}/change-password`,
      {
        oldPassword,
        newPassword,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${temporaryToken}`,
        },
      }
    );

    const data = await handleResponse(response);

    // Save the new permanent token after successful password change
    if (data.token) {
      // Import saveAuthToken function at runtime to avoid circular dependencies
      const { saveAuthToken } = await import("@/helpers/authHeader.js");
      saveAuthToken(data.token);
    }

    return data;
  } catch (error) {
    console.error("Error changing password during login:", error);
    return Promise.reject(error);
  }
}

/**
 * Get the current password policy from the server
 * @returns {Promise} - Promise resolving to password policy object
 */
async function getPasswordPolicy() {
  try {
    const authorization = getAuthHeader();
    const response = await axios.get(
      `${KUrls.BASE_REST_URL}${userEndpoint}/password-policy`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: authorization,
        },
      }
    );

    const data = await handleResponse(response);
    return data;
  } catch (error) {
    console.error("Error fetching password policy:", error);
    return Promise.reject(error);
  }
}
